import { useState } from 'react';
import { ScrollArea } from "@/components/ui/scroll-area"
import { MenuItem as IfCondition } from '@src/components/custom-nodes/IfConditionNode';
import { MenuItem as GenericItem } from '@src/components/custom-nodes/generic-node/GenericNode.tsx';
import { Input } from '@/components/ui/input';
import { SidebarSection } from '@src/components/sidebar/SidebarSection';
import type { NodesBaseConfig, NodesDefinition, SidebarSectionProps } from '@src/scenario-editor/render-utils/types';
import { dndItemAtom, dndStore } from '@src/atoms/dnd';
import { usePreventScrollNavigation } from '@src/scenario-editor/hooks/use-prevent-scroll-navigation.hook';
import { isDuplicateModifier } from '@src/components/custom-nodes/generic-node/hooks/duplicate-modifier';

const getRendererByType = (type: string) => {
  if (type === 'IfConditionNode') {
    return IfCondition;
  }

  return GenericItem;
}

interface SidebarProps {
  nodesConfig?: NodesBaseConfig[]
  nodesDefinition?: NodesDefinition
}
export const Sidebar = ({ nodesConfig = [], nodesDefinition = {} }: SidebarProps) => {
  const [search, setSearch] = useState('');

  usePreventScrollNavigation();

  const onDragStart = (event: React.DragEvent<HTMLDivElement>, serializedNode: string) => {
    event.dataTransfer.setData('application/reactflow', serializedNode);
    // Must agree with the `dropEffect` chosen on dragover - see isDuplicateModifier.
    // A palette drag always creates a new node, but the two effects still have to
    // match or the browser cancels the drop when the modifier happens to be held.
    event.dataTransfer.effectAllowed = isDuplicateModifier(event) ? 'copy' : 'move';
    dndStore.set(dndItemAtom, serializedNode);
    localStorage.setItem('dndItem', serializedNode);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value);
  };

  return (
    <aside className="bg-background border-l border-separator p-4 w-[350px] h-full">
      {/* LF360-1823 (#114): Input за замовчуванням h-9 (36px) - рівно як айтем палітри після цієї ж
    правки, через що пошук читався як ще один елемент списку. ПРИПУЩЕННЯ про намір, не виміряна
    вимога: опускаємо до 32px. Відкат - прибрати className. */}
      <Input iconType="search" onChange={handleChange} placeholder="Find action" className="h-8" />
      {/* LF360-1823 (#110 + #111): aside уже має p-4 (16px), тож pr-4 тут робив правий відступ 32px
    і відсував скролбар від краю. Один клас закривав обидва зауваження. */}
      <ScrollArea className="mt-2" style={{ height: 'calc(100% - 35px)' }}>
        {nodesConfig.map((section, index) => {
          const sectionItems = section.items.map((item) => {
            let relatedNodeDefinition = nodesDefinition[item.nodeType];
            if(!relatedNodeDefinition) {
              const nodeDefinitionPatterns = Object.keys(nodesDefinition).filter(pattern => pattern.includes('*'));
              const nodeDefinitionPatternKey = nodeDefinitionPatterns.find(pattern => item.nodeType.includes(pattern.split('*')[0]));
              relatedNodeDefinition = nodesDefinition[nodeDefinitionPatternKey ?? ''];
            }
            if (relatedNodeDefinition) {
              return {
                ...item,
                ...relatedNodeDefinition,
                handles: {
                  t: true,
                  b: true,
                },
                renderer: getRendererByType(item.type),
              };
            }
            return {
              ...item,
              handles: {
                t: true,
                b: true,
              },
              renderer: getRendererByType(item.type),
            };
          }) as SidebarSectionProps['items'];
          return (
            <SidebarSection key={index} searchQuery={search} items={sectionItems} title={section.title} onDragStart={onDragStart} />
          );
        })}
      </ScrollArea>
    </aside>
  );
};
